import BlogPostLayout from "../BlogPostLayout"
import { BlogPostMetadata } from "./index"
import { APP_NAME } from "@/lib/appConfig"

export const metadata: BlogPostMetadata = {
    slug: "navigating-security-deposits-deductions",
    title: "Navigating Security Deposits: What You Can and Cannot Deduct",
    excerpt: "Security deposit disputes are the #1 cause of landlord-tenant lawsuits. Learn exactly what qualifies as 'normal wear and tear' and how to legally protect your assets.",
    author: `${APP_NAME} Legal Insights`,
    date: "2026-03-01",
    readTime: "6 min read",
    coverImage: "https://images.unsplash.com/photo-1589829085413-56de8ae18c73?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    tags: ["Legal", "Tenant Management", "Operations"],
}

export default function BlogPost() {

    return (
        <BlogPostLayout metadata={metadata}>
            <h2 className="text-2xl md:text-3xl font-extrabold text-foreground mt-12 mb-6 border-b border-border/20 pb-4">The Security Deposit Minefield</h2>
            <p className="leading-relaxed mb-6 font-medium text-foreground/80">
                Across the United States and Europe, security deposits are heavily regulated. In many jurisdictions, unlawfully withholding a tenant's deposit can result in the landlord being sued for <strong>double or triple damages</strong> (treble damages).
            </p>
            <p className="leading-relaxed mb-6 font-medium text-foreground/80">
                The line between "tenant damage" and "normal wear and tear" is historically subjective. As a landlord, it is your legal burden to prove that the deduction was necessary. This requires meticulous record-keeping and an ironclad move-out protocol.
            </p>

            <h3 className="text-xl md:text-2xl font-bold text-primary mt-10 mb-5">"Normal Wear and Tear" Defined</h3>
            <p className="leading-relaxed mb-6 font-medium text-foreground/80">
                You cannot charge a tenant for the natural degradation of a property that occurs through normal, everyday use over time.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                <div className="bg-green-500/10 border border-green-500/20 p-6 rounded-2xl">
                    <h4 className="font-bold text-green-700 dark:text-green-400 mb-2">Examples of Wear & Tear (Cannot Deduct)</h4>
                    <ul className="text-sm space-y-2 mb-0">
                        <li className="leading-relaxed">• Faded paint or minor scuffs on the wall.</li>
                        <li className="leading-relaxed">• Worn traffic patterns on carpets.</li>
                        <li className="leading-relaxed">• Faded or slightly frayed blinds from sun exposure.</li>
                        <li className="leading-relaxed">• Loose door handles or cabinet hinges.</li>
                    </ul>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-2xl">
                    <h4 className="font-bold text-red-700 dark:text-red-400 mb-2">Examples of Tenant Damage (Can Deduct)</h4>
                    <ul className="text-sm space-y-2 mb-0">
                        <li className="leading-relaxed">• Large holes in the drywall or unauthorized paint jobs.</li>
                        <li className="leading-relaxed">• Stains, burns, or pet urine on the carpets.</li>
                        <li className="leading-relaxed">• Broken windows or missing blinds.</li>
                        <li className="leading-relaxed">• Deliberately removed doors or severe appliance negligence.</li>
                    </ul>
                </div>
            </div>

            <h3 className="text-xl md:text-2xl font-bold text-primary mt-10 mb-5">The 3 Rules for Legal Deductions</h3>

            <h4 className="text-lg md:text-xl font-semibold text-secondary-foreground mt-8 mb-4">1. The Itemized Deduction Letter</h4>
            <p className="leading-relaxed mb-6 font-medium text-foreground/80">
                If you are withholding any portion of the deposit, you must send the tenant an itemized list of deductions alongside the remaining balance within the statutory window (typically 14 to 30 days depending on your state).
            </p>
            <p className="leading-relaxed mb-6 font-medium text-foreground/80">
                You cannot broadly deduct "$500 for cleaning." You must provide the exact invoice from the cleaning company to justify the deduction.
            </p>

            <h4 className="text-lg md:text-xl font-semibold text-secondary-foreground mt-8 mb-4">2. The Move-In/Move-Out Inspection Formula</h4>
            <p className="leading-relaxed mb-6 font-medium text-foreground/80">
                You cannot prove a tenant caused a stain on the carpet if you cannot prove the carpet was pristine when they moved in.
            </p>
            <p className="leading-relaxed mb-6 font-medium text-foreground/80">
                <strong>The {APP_NAME} Solution:</strong> Before handing over the keys, landlords using <em>{APP_NAME}</em> require the tenant to fill out a digital Move-In Condition Report, complete with timestamps and photos, uploaded securely to the property's profile. During the move-out inspection, you simply load the profile side-by-side.
            </p>

            <h4 className="text-lg md:text-xl font-semibold text-secondary-foreground mt-8 mb-4">3. You Cannot Better Your Position</h4>
            <p className="leading-relaxed mb-6 font-medium text-foreground/80">
                If a tenant ruins a 10-year-old carpet, you cannot deduct the cost of a brand-new carpet from their deposit. Carpets generally have a useful IRS lifespan of 5 years. A 10-year-old carpet is technically worth zero dollars. Attempting to charge the tenant for a massive upgrade is illegal. You must prorate the cost based on the remaining useful life of the damaged item.
            </p>

            <h2 className="text-2xl md:text-3xl font-extrabold text-foreground mt-12 mb-6 border-b border-border/20 pb-4">The Ultimate Protection is Software</h2>
            <p className="leading-relaxed mb-6 font-medium text-foreground/80">
                The entire concept of a security deposit relies on an auditable trail of documentation. Property management software eliminates the "he said, she said" dynamic. By enforcing photo uploads during move-in, logging mid-lease maintenance requests, and storing tenant communications centrally, <strong>{APP_NAME}</strong> provides the irrefutable proof landlords need to confidently protect their investments.
            </p>
        </BlogPostLayout>
    )
}
